/**
 * Unified Settings Page
 * Consolidates labels, automation, and account settings
 */

import { IconTag, IconBolt, IconUser } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { APP_VERSION } from '@/lib/version'

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || ''
import { LabelsSection } from './settings/labels-section'
import { AutomationSection } from './settings/automation-section'
import { AccountSection } from './settings/account-section'

type SettingsSection = 'labels' | 'automation' | 'account'

const SETTINGS_SECTIONS = [
  { id: 'labels' as const, label: 'Labels', icon: IconTag },
  { id: 'automation' as const, label: 'Automation', icon: IconBolt },
  { id: 'account' as const, label: 'Account', icon: IconUser },
]

interface SettingsPageProps {
  activeSection: SettingsSection
  billingSuccess?: boolean
  billingCanceled?: boolean
  autoStartTrial?: boolean
  billingRequired?: boolean
  checkoutPlan?: 'pro' | 'business'
  checkoutInterval?: 'monthly' | 'yearly'
}

export function SettingsPage({
  activeSection,
  billingSuccess,
  billingCanceled,
  autoStartTrial,
  billingRequired,
  checkoutPlan,
  checkoutInterval,
}: SettingsPageProps) {
  const navigate = useNavigate()
  const [apiVersion, setApiVersion] = useState<string | null>(null)

  useEffect(() => {
    const healthUrl = CF_WORKER_URL ? `${CF_WORKER_URL}/health` : '/health'
    fetch(healthUrl)
      .then(res => res.json())
      .then(data => setApiVersion(data.version || null))
      .catch(() => setApiVersion(null))
  }, [])

  const handleSectionChange = (sectionId: SettingsSection) => {
    if (billingRequired && sectionId !== 'account') return
    navigate({ to: '/settings', search: { section: sectionId } })
  }

  const visibleSections = billingRequired
    ? SETTINGS_SECTIONS.filter((section) => section.id === 'account')
    : SETTINGS_SECTIONS

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-lg font-medium mb-6">Settings</h1>

      <div className="flex gap-8">
        {/* Sidebar */}
        <nav className="w-40 shrink-0">
          <ul className="space-y-0.5">
            {visibleSections.map((section) => {
              const Icon = section.icon
              const isActive = activeSection === section.id

              return (
                <li key={section.id}>
                  <button
                    onClick={() => handleSectionChange(section.id)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs transition-colors ${
                      isActive
                        ? 'text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {section.label}
                  </button>
                </li>
              )
            })}
          </ul>

          {/* Version footer */}
          <div className="mt-8 pt-4 border-t border-border/50">
            <div className="text-[10px] text-muted-foreground/50 font-mono space-y-0.5">
              <div>ui: {APP_VERSION}</div>
              {apiVersion && <div>api: {apiVersion}</div>}
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeSection === 'labels' && <LabelsSection />}
          {activeSection === 'automation' && <AutomationSection />}
          {activeSection === 'account' && (
            <AccountSection
              billingSuccess={billingSuccess}
              billingCanceled={billingCanceled}
              autoStartTrial={autoStartTrial}
              billingRequired={billingRequired}
              checkoutPlan={checkoutPlan}
              checkoutInterval={checkoutInterval}
            />
          )}
        </div>
      </div>
    </div>
  )
}
